import { FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';
import { assertExhaustive } from '../../../../../utils';
import { InputSign } from '../../../sharedComponents/InputSign';
import { OptionalLabel } from '../../../sharedComponents/OptionalLabel';
import { AccessoryType } from '../accessory.types';
import { ServiceOrder, ServiceOrderStatus } from '../serviceOrder.types';
import { DatePickerWithLabel } from '../../shared/sidebar/DatePickerWithLabel';
import { HighlightedFormContainer } from '../../shared/sidebar/HighlightedFormContainer';
import { AccessoriesInput, InvalidAccessoriesAlert } from './AccessoriesInput';
import type {
    ExecutionDateErrorType,
    ServiceOrderExecutionFormErrors,
    ServiceOrderSidebarExecutionDetails,
    SidebarFormFieldError,
} from './useServiceOrderExecutionForm';
import { ServiceDescription } from '../../shared/sidebar/ServiceDescription';

const hasInvalidAccessories = (errors: ServiceOrderExecutionFormErrors) =>
    Object.keys(errors).some((key) => Object.values<string>(AccessoryType).includes(key));

type ServiceOrderSidebarBodyProps = {
    serviceOrder: ServiceOrder;
    setExecutionDetailsFormData: (formData: ServiceOrderSidebarExecutionDetails) => void;
    executionDetailsFormData: ServiceOrderSidebarExecutionDetails;
    errors: ServiceOrderExecutionFormErrors;
};

export const ServiceOrderSidebarBody: FunctionComponent<ServiceOrderSidebarBodyProps> = ({
    setExecutionDetailsFormData,
    executionDetailsFormData,
    serviceOrder,
    errors,
}) => {
    const isDisabled =
        serviceOrder.status === ServiceOrderStatus.CANCELLED || serviceOrder.isFinalized || serviceOrder.isArchived;

    return (
        <HighlightedFormContainer instructionMessageId="outboundPortal.serviceOrder.sidebar.pleaseProvide">
            <div className="margin-top-20 margin-bottom-20">
                <ServiceDescription service={serviceOrder} />
            </div>
            {hasInvalidAccessories(errors) && (
                <div>
                    <InvalidAccessoriesAlert messageId="outboundPortal.common.invalidAccessory" />
                </div>
            )}
            {serviceOrder.vehicle.requestedAccessories.length > 0 && (
                <div className="margin-bottom-20">
                    <AccessoriesInput
                        requestedAccessories={serviceOrder.vehicle.requestedAccessories}
                        onChange={(accessories) =>
                            setExecutionDetailsFormData({ ...executionDetailsFormData, accessories })
                        }
                        collectedAccessoriesFormData={executionDetailsFormData.accessories}
                        accessoryErrors={errors}
                        isDisabled={isDisabled}
                    />
                </div>
            )}
            <div className="margin-bottom-20 display-flex gap-10">
                <DatePickerWithLabel
                    labelMessageId="outboundPortal.serviceOrder.sidebar.executionStartDate"
                    value={executionDetailsFormData.startedAt}
                    onChange={(date) => setExecutionDetailsFormData({ ...executionDetailsFormData, startedAt: date })}
                    error={getDateErrorMessage(errors.startedAt)}
                    timeZone={serviceOrder.serviceLocation.timeZone}
                    isDisabled={isDisabled}
                />
                <DatePickerWithLabel
                    labelMessageId="outboundPortal.serviceOrder.sidebar.executionEndDate"
                    value={executionDetailsFormData.completedAt}
                    onChange={(date) => setExecutionDetailsFormData({ ...executionDetailsFormData, completedAt: date })}
                    error={getDateErrorMessage(errors.completedAt)}
                    timeZone={serviceOrder.serviceLocation.timeZone}
                    isDisabled={isDisabled}
                    alignRight
                />
            </div>
            <div>
                <OptionalLabel isOptional htmlFor="generalInformation">
                    <FormattedMessage id="outboundPortal.serviceOrder.sidebar.generalInformation" />
                </OptionalLabel>
                <textarea
                    id="generalInformation"
                    disabled={isDisabled}
                    className="width-100pct form-control"
                    style={{ resize: 'none' }}
                    rows={5}
                    maxLength={255}
                    value={executionDetailsFormData.generalInformation ?? ''}
                    onChange={(text) => {
                        setExecutionDetailsFormData({
                            ...executionDetailsFormData,
                            generalInformation: text.target.value,
                        });
                    }}
                />
            </div>
        </HighlightedFormContainer>
    );
};

const getDateErrorMessage = (error: SidebarFormFieldError<ExecutionDateErrorType> | undefined) => {
    if (error === undefined) {
        return undefined;
    }

    switch (error.type) {
        case 'invalidDate':
            return <InputSign messageId="outboundPortal.common.invalidDate" glyph="rioglyph-error-sign" />;
        case 'dateInFuture':
            return (
                <InputSign
                    messageId="outboundPortal.serviceOrder.sidebar.executionDate.inTheFuture"
                    glyph="rioglyph-error-sign"
                />
            );
        case 'endDateBeforeStartDateError':
            return (
                <InputSign
                    messageId="outboundPortal.serviceOrder.sidebar.executionDate.endDateBeforeStartDateError"
                    glyph="rioglyph-error-sign"
                />
            );
        case 'removedDate':
            return (
                <InputSign
                    messageId="outboundPortal.serviceOrder.sidebar.executionDate.removedDate"
                    glyph="rioglyph-error-sign"
                />
            );
        case 'oneMandatory':
            return (
                <InputSign
                    messageId="outboundPortal.serviceOrder.sidebar.executionDate.oneMandatory"
                    glyph="rioglyph-error-sign"
                />
            );
        default:
            assertExhaustive(error.type);
    }
};
